import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Sparkles } from "lucide-react";

/**
 * DaySplash — full-screen welcome shown on the first login of each calendar
 * day. Renders a beautifully-animated Day Card with:
 *
 *   • Intro line   — "Your daily card is here, {name}. Come back tomorrow for
 *                     a new one!"
 *   • Animated card — 3D tilt-in, floating glow, subtle scale pulse
 *   • Outro line   — "Have a great day ahead."
 *
 * Auto-dismisses after 15 s (Skip button top-right). "First login of the day"
 * is tracked in localStorage under `daySplashLastShown = YYYY-MM-DD` so a
 * later login the same day skips the splash.
 */
const STORAGE_KEY = "daySplashLastShown";
const AUTO_DISMISS_MS = 15_000;

export default function DaySplash({ user }) {
  const [card, setCard] = useState(null);
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let dismissTimer = null;
    let leaveTimer = null;
    (async () => {
      try {
        const { data } = await api.get("/day-cards/mine");
        if (cancelled) return;
        // Iter 36.9.7 — Server-side per-user tracking is now the source of
        // truth (`last_day_splash_date` on the user doc). localStorage stays
        // as a client-side short-circuit for the same tab within the day.
        const already = localStorage.getItem(STORAGE_KEY);
        if (data.seen_today || already === data.today) {
          // Keep localStorage in sync with what the server thinks.
          if (data.seen_today) localStorage.setItem(STORAGE_KEY, data.today);
          return;
        }
        setCard(data);
        setVisible(true);
        // Persist client-side immediately AND tell the server we're showing
        // it so any parallel session (other tab / browser) suppresses the
        // splash from here on.
        localStorage.setItem(STORAGE_KEY, data.today);
        api.post("/day-cards/mark-seen").catch(() => {});
        requestAnimationFrame(() => setEntered(true));
        // Auto-dismiss: start leave animation just before the timer fires so
        // the exit transition doesn't feel abrupt.
        dismissTimer = window.setTimeout(() => {
          setLeaving(true);
          leaveTimer = window.setTimeout(() => setVisible(false), 500);
        }, AUTO_DISMISS_MS);
      } catch { /* nice-to-have — silently drop */ }
    })();
    return () => {
      cancelled = true;
      if (dismissTimer) window.clearTimeout(dismissTimer);
      if (leaveTimer) window.clearTimeout(leaveTimer);
    };
  }, [user]);

  const skip = () => {
    setLeaving(true);
    window.setTimeout(() => setVisible(false), 400);
  };

  if (!visible || !card) return null;

  const firstName = (user?.name || "friend").split(" ")[0];

  return (
    <div
      className={`fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center px-6 transition-opacity duration-500 ${entered && !leaving ? "opacity-100" : "opacity-0"}`}
      data-testid="day-splash-overlay"
    >
      <button
        onClick={skip}
        aria-label="Skip"
        className="absolute top-6 right-6 text-slate-400 hover:text-slate-700 text-sm flex items-center gap-1.5"
        data-testid="day-splash-skip"
      >
        Skip <X className="h-4 w-4" />
      </button>

      {/* Intro line */}
      <p
        className={`text-slate-500 text-sm sm:text-base tracking-wide mb-6 sm:mb-8 transition-all duration-700 ${entered ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"}`}
        data-testid="day-splash-intro"
      >
        Your daily card is here, <span className="font-semibold text-slate-800">{firstName}</span>. Come back tomorrow for a new one!
      </p>

      {/* Animated card */}
      <div className="relative" style={{ perspective: 1000 }}>
        {/* soft floating glow */}
        <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-amber-200/50 via-rose-200/40 to-purple-200/50 blur-3xl scale-105 animate-daycard-glow" />
        <div
          className={`relative rounded-3xl border border-amber-200/70 bg-gradient-to-br from-amber-50 via-white to-rose-50 shadow-[0_30px_60px_-20px_rgba(251,191,36,0.35)] max-w-3xl w-full transition-all duration-[900ms] ease-out ${entered ? "opacity-100 rotate-0 translate-y-0 scale-100" : "opacity-0 -rotate-6 translate-y-6 scale-95"} animate-daycard-float`}
          data-testid="day-splash-card"
        >
          <div className="px-8 sm:px-12 py-10 sm:py-14 text-center relative overflow-hidden">
            {/* decorative sparkle */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-200 rounded-full opacity-30 blur-2xl pointer-events-none" />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-rose-200 rounded-full opacity-30 blur-2xl pointer-events-none" />

            <div className={`inline-flex items-center gap-2 text-amber-500 text-[11px] uppercase tracking-[0.3em] font-semibold mb-8 transition-all duration-500 delay-150 ${entered ? "opacity-100" : "opacity-0"}`}>
              <Sparkles className="h-3.5 w-3.5 animate-spin-slow" /> Day Card <Sparkles className="h-3.5 w-3.5 animate-spin-slow" />
            </div>

            <div
              className={`text-slate-900 font-display leading-[1.15] tracking-tight transition-all duration-700 delay-300 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
              style={{ fontSize: "clamp(1.75rem, 4.5vw, 3.5rem)" }}
              data-testid="day-splash-message"
            >
              <span className="text-amber-400/80 font-serif align-top mr-2 text-[1.6em] leading-none">“</span>
              {card.message}
              <span className="text-amber-400/80 font-serif align-bottom ml-2 text-[1.6em] leading-none">”</span>
            </div>
          </div>
        </div>
      </div>

      {/* Outro line */}
      <p
        className={`mt-8 sm:mt-10 text-slate-600 text-base sm:text-lg italic transition-all duration-700 delay-500 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
        data-testid="day-splash-outro"
      >
        Have a great day ahead.
      </p>
    </div>
  );
}
